import React, { useEffect, useRef } from "react";
import {
  IoArrowBack,
  IoArrowForward,
  IoCameraOutline,
  IoImagesOutline,
  IoStar,
  IoTrashOutline,
} from "react-icons/io5";

export type CarPhotoItem = {
  id: string;
  previewUrl: string;
  remoteUrl?: string;
  file?: File;
};

type CarPhotoManagerProps = {
  photos: CarPhotoItem[];
  minimum: number;
  maximum?: number;
  error?: string;
  onChange: (photos: CarPhotoItem[]) => void;
};

const makePhotoId = () =>
  globalThis.crypto?.randomUUID?.() ||
  `photo-${Date.now()}-${Math.random().toString(16).slice(2)}`;

function CarPhotoManager({
  photos,
  minimum,
  maximum = 10,
  error,
  onChange,
}: CarPhotoManagerProps) {
  const galleryInput = useRef<HTMLInputElement | null>(null);
  const cameraInput = useRef<HTMLInputElement | null>(null);
  const localUrls = useRef(new Set<string>());

  useEffect(
    () => () => {
      localUrls.current.forEach((url) => URL.revokeObjectURL(url));
      localUrls.current.clear();
    }, []
  );

  useEffect(() => {
    const visibleUrls = new Set(photos.map((photo) => photo.previewUrl));
    localUrls.current.forEach((url) => {
      if (!visibleUrls.has(url)) {
        URL.revokeObjectURL(url);
        localUrls.current.delete(url);
      }
    });
  }, [photos]);

  const addFiles = (files: FileList | null) => {
    if (!files || photos.length >= maximum) return;

    const existingFiles = new Set(
      photos
        .filter((photo) => photo.file)
        .map((photo) => `${photo.file?.name}:${photo.file?.size}`)
    );
    const next = Array.from(files)
      .filter((file) => file.type.startsWith("image/"))
      .filter((file) => !existingFiles.has(`${file.name}:${file.size}`))
      .slice(0, maximum - photos.length)
      .map((file) => {
        const previewUrl = URL.createObjectURL(file);
        localUrls.current.add(previewUrl);
        return { id: makePhotoId(), previewUrl, file };
      });

    if (next.length) onChange([...photos, ...next]);
  };

  const remove = (index: number) => {
    if (photos.length <= minimum) return;
    const target = photos[index];
    if (target.file) {
      URL.revokeObjectURL(target.previewUrl);
      localUrls.current.delete(target.previewUrl);
    }
    onChange(photos.filter((_, photoIndex) => photoIndex !== index));
  };

  const move = (from: number, to: number) => {
    if (to < 0 || to >= photos.length || from === to) return;
    const next = [...photos];
    const [photo] = next.splice(from, 1);
    next.splice(to, 0, photo);
    onChange(next);
  };

  const setPrimary = (index: number) => move(index, 0);

  return (
    <section className={ClassName.card}>
      <div className={ClassName.header}>
        <div>
          <span className={ClassName.eyebrow}>LISTING PHOTOS</span>
          <h2 className={ClassName.title}>Photos</h2>
          <p className={ClassName.helper}>
            The first photo is the cover. Keep {minimum}-{maximum} photos.
          </p>
        </div>
        <span className={ClassName.count}>
          {photos.length}/{maximum}
        </span>
      </div>

      {error ? <div className={ClassName.error}>{error}</div> : null}

      <div className={ClassName.addRow}>
        <button
          type="button"
          className={ClassName.addButton}
          disabled={photos.length >= maximum}
          onClick={() => galleryInput.current?.click()}
        >
          <IoImagesOutline size={19} /> Add photos
        </button>
        <button
          type="button"
          className={ClassName.cameraButton}
          disabled={photos.length >= maximum}
          onClick={() => cameraInput.current?.click()}
        >
          <IoCameraOutline size={19} /> Take photo
        </button>
        <input
          ref={galleryInput}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(event) => {
            addFiles(event.target.files);
            event.target.value = "";
          }}
        />
        <input
          ref={cameraInput}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(event) => {
            addFiles(event.target.files);
            event.target.value = "";
          }}
        />
      </div>

      {!photos.length ? (
        <button
          type="button"
          className={ClassName.empty}
          onClick={() => galleryInput.current?.click()}
        >
          <IoImagesOutline size={28} />
          <strong>Add listing photos</strong>
          <span>JPG, PNG, or WebP</span>
        </button>
      ) : (
        <div className={ClassName.grid}>
          {photos.map((photo, index) => (
            <article
              key={photo.id}
              className={`${ClassName.photoCard} ${
                index === 0 ? ClassName.primaryCard : ""
              }`}
            >
              <div className={ClassName.imageShell}>
                <img
                  src={photo.previewUrl}
                  alt={`Car ${index + 1}`}
                  className={ClassName.image}
                />
                {index === 0 ? (
                  <span className={ClassName.primaryBadge}>
                    <IoStar size={12} /> Cover
                  </span>
                ) : null}
                {photo.file ? <span className={ClassName.newBadge}>New</span> : null}
              </div>
              <div className={ClassName.actions}>
                {index > 0 ? (
                  <button
                    type="button"
                    className={ClassName.iconButton}
                    title="Move left"
                    onClick={() => move(index, index - 1)}
                  >
                    <IoArrowBack />
                  </button>
                ) : null}
                {index < photos.length - 1 ? (
                  <button
                    type="button"
                    className={ClassName.iconButton}
                    title="Move right"
                    onClick={() => move(index, index + 1)}
                  >
                    <IoArrowForward />
                  </button>
                ) : null}
                {index > 0 ? (
                  <button
                    type="button"
                    className={ClassName.primaryButton}
                    onClick={() => setPrimary(index)}
                  >
                    Set cover
                  </button>
                ) : (
                  <span className={ClassName.coverText}>Main image</span>
                )}
                <button
                  type="button"
                  className={ClassName.removeButton}
                  title={
                    photos.length <= minimum
                      ? `At least ${minimum} photos are required`
                      : "Remove photo"
                  }
                  disabled={photos.length <= minimum}
                  onClick={() => remove(index)}
                >
                  <IoTrashOutline />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export default CarPhotoManager;

const ClassName = {
  card: "rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm sm:p-6",
  header: "flex items-start justify-between gap-4",
  eyebrow: "text-[10px] font-bold tracking-[0.2em] text-primary",
  title: "mt-1 text-xl font-bold text-slate-950",
  helper: "mt-1 text-sm leading-5 text-slate-500",
  count: "rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600",
  error: "mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700",
  addRow: "mt-5 flex flex-wrap gap-2",
  addButton:
    "inline-flex h-11 items-center gap-2 rounded-xl bg-slate-950 px-4 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40",
  cameraButton:
    "inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40",
  empty:
    "mt-4 flex min-h-44 w-full flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500 [&>strong]:text-slate-800",
  grid: "mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3",
  photoCard:
    "overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow hover:shadow-md",
  primaryCard: "ring-2 ring-slate-950 ring-offset-2",
  imageShell: "relative aspect-[4/3] overflow-hidden bg-slate-100",
  image: "h-full w-full object-cover",
  primaryBadge:
    "absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-slate-950 px-2.5 py-1 text-[10px] font-bold text-white",
  newBadge:
    "absolute right-2 top-2 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold text-primary shadow-sm",
  actions: "flex min-h-12 items-center gap-1 border-t border-slate-100 px-2 py-2",
  iconButton:
    "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-950",
  primaryButton:
    "min-w-0 flex-1 rounded-lg px-2 py-2 text-xs font-bold text-slate-700 hover:bg-slate-100",
  coverText: "min-w-0 flex-1 px-2 text-xs font-bold text-slate-500",
  removeButton:
    "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-25",
};
